import { useState } from 'react';
import Tooltip from '@/components/Tooltip';
import { PopoverShell } from '@/components/PopoverShell';
import { GgcButton } from '@/components/ui/ggc/button';
import { useI18nText } from '@/lib/utils/i18nText';

/**
 * A neutral-gray tag chip with an inline × button. Clicking × opens a small
 * confirm popover anchored to the chip (portaled, so it escapes the table
 * cell's overflow:hidden). Confirming runs `onRemove` and closes on success.
 * Gray rather than a tone color (e.g. red) — a tag is just a plain label, not
 * a status/warning, so it shouldn't borrow red's "danger" connotation.
 */
export function RemovableTag({ tag, onRemove }: {
  tag: string;
  onRemove: () => Promise<void>;
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleConfirm = async () => {
    setRemoving(true);
    try {
      await onRemove();
      setOpen(false);
    } catch { /* surfaced by the global error handler */ } finally {
      setRemoving(false);
    }
  };

  const removeButton = (
    <button
      type="button"
      className="ggc-tag__remove"
      aria-label={t('Delete tag', '태그 삭제')}
      onClick={(e) => { e.stopPropagation(); setOpen(true); }}
    >
      <i className="fa fa-times" />
    </button>
  );

  return (
    <PopoverShell
      open={open}
      onOpenChange={(v) => { if (!removing) setOpen(v); }}
      anchor={
        <span className="ggc-tag ggc-tag--default ggc-tag--removable">
          {tag}
          {/* Hide the hover hint once the confirm popover is open so the two don't stack. */}
          {open ? removeButton : (
            <Tooltip essential content={t('Click to delete this tag', '클릭하면 이 태그가 삭제됩니다')}>
              {removeButton}
            </Tooltip>
          )}
        </span>
      }
      align="center"
      collisionPadding={8}
      shadow="lg"
      arrow
      className="tag-delete-popover"
    >
      <div className="tag-delete-popover__msg">
        {t('Delete tag', '태그 삭제')} <strong>{tag}</strong>?
      </div>
      <div className="tag-delete-popover__note">
        <i className="fa fa-info-circle" aria-hidden />
        <span>
          {t(
            'If this tag matches a campaign/roster name, the influencer is also removed from that list via tag auto-sync.',
            '캠페인·로스터 이름과 동일한 태그라면, 태그 자동 동기화로 해당 목록에서도 제외됩니다.',
          )}
        </span>
      </div>
      <div className="tag-delete-popover__actions">
        <GgcButton variant="secondary" size="xs" disabled={removing} onClick={() => setOpen(false)}>
          {t('Cancel', '취소')}
        </GgcButton>
        <GgcButton variant="danger" size="xs" disabled={removing} onClick={() => void handleConfirm()}>
          {removing
            ? <><i className="fa fa-spinner fa-spin mr-1" />{t('Deleting...', '삭제 중...')}</>
            : <><i className="fa fa-trash mr-1" />{t('Delete', '삭제')}</>}
        </GgcButton>
      </div>
    </PopoverShell>
  );
}
